import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import {
  UTILITY_PROVIDER_TYPE_LINK,
  UTILITY_PROVIDER_TYPE_PENDING_SETUP,
  UTILITY_PROVIDER_TYPE_PLANS,
  UTILITY_TYPE_ID_INTERNET,
} from 'common/utility/constants';

import emptyFunction from 'common/util/emptyFunction';

import { SR_API_ACTION_UTILITY_PROVIDER_GET_BY_AUTOCOMPLETE } from 'api/utilityActionTypes';

import { hasRoleAdmin } from 'common/utility/userRoles';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import requestUtilityApi from 'client/actions/requestUtilityApi';
import useDispatch from 'client/hooks/useDispatch';
import useBranch from 'client/hooks/useBranch';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrExternalLink from 'client/components/SrExternalLink';
import SrUtilityProfitOptionCard from 'client/components/SrUtilityProfitOptionCard/SrUtilityProfitOptionCard';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';

const FREEDOM_GPT = 'https://freedomgpt-git-staging-ageofaicapital-s-team.vercel.app/browser-call';

const getFormFields = ({ onFindUtilityProviders, autoFocus } = {}) => [
  {
    id: 'searchInput',
    title: 'Search utility company',
    type: 'autocomplete',
    properties: {
      placeholder: 'type company name here...',
      choices: [],
      onSuggestionsFetchRequested: ({ value }) => {
        return onFindUtilityProviders({ searchInput: value });
      },
      filterFunc: () => {
        return true;
      },
      autoFocus: autoFocus,
    },
  },
];

export const FreedomGPTLink = ({ utilityType, utilityProvider, addressDisplay }) => {
  const link =
    FREEDOM_GPT +
    `?utilityName=${utilityProvider.title}` +
    `&utilityNumber=1${utilityProvider.phone}` +
    `&utilityAddress=${addressDisplay}` +
    `&utilityType=${utilityType?.label}`;

  return (
    <SrExternalLink link={link}>
      <SrText font="greycliff" color="blue300" size="13" weight="600" cursor="pointer">
        Call with FreedomGPT
      </SrText>
    </SrExternalLink>
  );
};

export const UPProviderSearchFormInner = ({
  autoFocus,
  addressDisplay,
  utilityType,
  onProviderSelected,
  onAddNew,
}) => {
  const [utilityProviderMatches, setUtilityProviderMatches] = useState([]);
  const dispatch = useDispatch();
  const { utilityUser } = useBranch({
    utilityUser: ['utilityUser'],
  });
  const isAdmin = hasRoleAdmin(utilityUser.roles);

  const onFindUtilityProviders = ({ searchInput }) => {
    if (!searchInput) {
      setUtilityProviderMatches([]);
      return;
    }

    dispatch(requestUtilityApi, {
      queryId: searchInput,
      action: SR_API_ACTION_UTILITY_PROVIDER_GET_BY_AUTOCOMPLETE,
      payload: {
        searchInput,
        requestingUserId: utilityUser.id,
        limit: 4,
        utilityTypeIds: utilityType ? [utilityType.id] : null,
        // TODO(@d-oliveros): Rename utilityProvider.type to utilityProvider.category
        types: [
          UTILITY_PROVIDER_TYPE_PLANS,
          UTILITY_PROVIDER_TYPE_LINK,
          UTILITY_PROVIDER_TYPE_PENDING_SETUP,
        ],
      },
    }).then((autocompletePredictions) => {
      setUtilityProviderMatches(autocompletePredictions || []);
    });
  };

  const formFields = getFormFields({
    onFindUtilityProviders,
    utilityProviderMatches,
    autoFocus,
  });

  return (
    <Formik
      initialValues={createInitialValues({ formFields })}
      enableReinitialize
      validate={createFormValidationFn(formFields)}
      onSubmit={emptyFunction}
      render={(formikBag) => {
        const searchInput = formikBag.values?.searchInput;

        return (
          <Form>
            {formFields.map((formField) => (
              <SrFormikField
                key={formField.id}
                formField={formField}
                formikBag={formikBag}
                theme={SR_FIELD_THEME_ADOBE}
              />
            ))}
            {searchInput && utilityProviderMatches.length === 0 && (
              <SrBox textAlign="center" paddingTop="20px">
                {(!utilityType || utilityType.id !== UTILITY_TYPE_ID_INTERNET) && onAddNew ? (
                  <SrText weight="800" font="greycliff" size="16">
                    {'That company does not currently exist in our system, '}
                    <SrText
                      font="greycliff"
                      weight="800"
                      size="16"
                      color="blue300"
                      decoration="underline"
                      cursor="pointer"
                      onClick={() => onAddNew({ formikBag })}
                    >
                      add the company here
                    </SrText>
                    .
                  </SrText>
                ) : (
                  <SrText weight="800" font="greycliff" size="16">
                    That company does not currently exist in our system.
                  </SrText>
                )}
              </SrBox>
            )}
            {searchInput &&
              utilityProviderMatches.map((utilityProvider) => {
                const providerLink = utilityProvider.url ? (
                  <SrExternalLink link={utilityProvider.url}>
                    <SrText
                      font="greycliff"
                      color="blue300"
                      size="13"
                      weight="600"
                      cursor="pointer"
                    >
                      {`${utilityProvider.url.slice(0, 25)}...`}
                    </SrText>
                  </SrExternalLink>
                ) : null;

                return (
                  <SrBox paddingTop="23px" key={utilityProvider.id}>
                    <SrUtilityProfitOptionCard
                      title={utilityProvider.title}
                      subtitle={
                        <>
                          {providerLink}
                          {isAdmin && addressDisplay && (
                            <>
                              <br />
                              <FreedomGPTLink
                                addressDisplay={addressDisplay}
                                utilityProvider={utilityProvider}
                                utilityType={utilityType}
                              />
                            </>
                          )}
                        </>
                      }
                      detailIconUrl={utilityType?.iconUrl || utilityProvider.utilityTypeIconUrl}
                      imageUrl={utilityType?.imageUrl || utilityProvider.utilityTypeImageUrl}
                      detailColor={
                        utilityType?.iconBackgroundColor ||
                        utilityProvider.utilityTypeIconBackgroundColor
                      }
                      onAddClick={() => {
                        setUtilityProviderMatches([]);
                        formikBag.setFieldValue('searchInput', '');
                        onProviderSelected(utilityProvider);
                      }}
                    />
                  </SrBox>
                );
              })}
          </Form>
        );
      }}
    />
  );
};

UPProviderSearchFormInner.propTypes = {
  utilityType: PropTypes.object,
  onProviderSelected: PropTypes.func.isRequired,
  onAddNew: PropTypes.func,
};

const UPProviderSearchForm = ({
  title,
  utilityType,
  addressDisplay,
  onClose,
  onAddNew,
  onProviderSelected,
}) => {
  return (
    <SrDarkModal onClose={onClose}>
      <SrModalContent width={412} innerStyle={{ padding: 0 }}>
        <SrBox textAlign="center" borderBottom="2px solid #e9ebec" padding="20px 0">
          <SrText
            font="greycliff"
            weight="800"
            lineHeight="25px"
            fontSize="25px"
            display="block"
            paddingBottom="8px"
          >
            {title || `Search${utilityType?.label ? ` ${utilityType.label}` : ''} providers`}
          </SrText>
          <SrText font="greycliff" weight="800" lineHeight="19px" fontSize="16px" display="block">
            {addressDisplay}
          </SrText>
        </SrBox>

        <SrBox padding="0 40px 40px">
          <UPProviderSearchFormInner
            autoFocus
            utilityType={utilityType}
            onAddNew={onAddNew}
            onProviderSelected={onProviderSelected}
          />
        </SrBox>
      </SrModalContent>
    </SrDarkModal>
  );
};

UPProviderSearchForm.propTypes = {
  title: PropTypes.string,
  utilityType: PropTypes.object,
  addressDisplay: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onAddNew: PropTypes.func.isRequired,
  onProviderSelected: PropTypes.func.isRequired,
};

UPProviderSearchForm.displayName = 'UPProviderSearchForm';

export default UPProviderSearchForm;
